import React, { useState, useEffect, useCallback } from 'react';

// ============================================================
// Szolgáltatói folyamatok (case_jobs) — csak staff látja.
// Kártyalista + új folyamat modal (partner multi-select szűrőkkel)
// + kártyánként státusz-léptetés, mezők, jegyzetek, sablon/email.
// ============================================================

// Kliens oldali tükör a backend TRANSITIONS-ról (gomb-megjelenítéshez;
// a szerver validál, ez csak UI kényelem)
const TRANSITIONS = {
  contact_pending:            ['quote_requested', 'cancelled'],
  quote_requested:            ['quote_received', 'cancelled'],
  quote_received:             ['awaiting_customer_approval', 'quote_accepted', 'cancelled'],
  awaiting_customer_approval: ['quote_accepted', 'quote_rejected', 'cancelled'],
  quote_rejected:             ['quote_requested', 'cancelled'],
  quote_accepted:             ['scheduled', 'cancelled'],
  scheduled:                  ['in_progress', 'cancelled'],
  in_progress:                ['on_hold', 'work_done'],
  on_hold:                    ['in_progress', 'cancelled'],
  work_done:                  ['accepted', 'work_rejected'],
  work_rejected:              ['in_progress'],
  accepted:                   ['invoiced'],
  invoiced:                   ['invoice_confirmed'],
  invoice_confirmed:          [],
  cancelled:                  [],
};
export const JOB_TERMINAL = ['invoice_confirmed', 'cancelled'];

// Nem-státuszváltás jellegű előzmény-bejegyzések ikonja (action mező)
const ACTION_ICON = {
  quote_template_copied: '📋',
  quote_email_sent:      '✉️',
  quote_email_failed:    '⚠️',
};
const RATEABLE = ['accepted', 'invoiced', 'invoice_confirmed', 'cancelled'];

const jobStatusChipClass = (s) => {
  if (['cancelled', 'quote_rejected', 'work_rejected'].includes(s)) return 'cs-chip jb-red';
  if (['invoice_confirmed', 'accepted'].includes(s)) return 'cs-chip jb-green';
  if (['in_progress', 'scheduled', 'work_done', 'invoiced'].includes(s)) return 'cs-chip jb-blue';
  if (s === 'on_hold' || s === 'awaiting_customer_approval') return 'cs-chip jb-orange';
  return 'cs-chip jb-grey';
};

const Stars = ({ value, onChange, size = 16 }) => (
  <span className="jb-stars">
    {[1, 2, 3, 4, 5].map(n => (
      <button key={n} type="button" className={`jb-star ${n <= (value || 0) ? 'on' : ''}`}
        style={{ fontSize: size }} onClick={onChange ? () => onChange(n) : undefined}
        disabled={!onChange}>★</button>
    ))}
  </span>
);

const catName = (raw, language) => {
  if (!raw) return '';
  try {
    const o = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return o[language] || o.en || Object.values(o).find(Boolean) || '';
  } catch { return String(raw); }
};

const fmtMoney = (v, cur) => (v == null ? '—' : `${Number(v).toLocaleString()} ${cur || 'EUR'}`);
const fmtDT = (d) => (d ? new Date(d).toLocaleString() : '—');

// ── ÚJ FOLYAMAT MODAL — partner multi-select ─────────────────
const NewJobModal = ({ api, caseId, t, language, isDark, onClose, onCreated }) => {
  const [cats, setCats]       = useState([]);
  const [areas, setAreas]     = useState([]);
  const [partners, setPartners] = useState([]);
  const [catF, setCatF]       = useState('');
  const [areaF, setAreaF]     = useState('');
  const [q, setQ]             = useState('');
  const [selected, setSelected] = useState(new Set());
  const [title, setTitle]     = useState('');
  const [desc, setDesc]       = useState('');
  const [needsApproval, setNeedsApproval] = useState(true);
  const [busy, setBusy]       = useState(false);
  const [err, setErr]         = useState(null);

  useEffect(() => {
    api.get('/partners/categories').then(r => setCats(r.data)).catch(() => {});
    api.get('/partners/areas').then(r => setAreas(r.data)).catch(() => {});
  }, [api]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (catF) params.set('category_id', catF);
    if (areaF) params.set('area_id', areaF);
    if (q) params.set('q', q);
    api.get(`/jobs/partners?${params}`).then(r => setPartners(r.data)).catch(() => {});
  }, [api, catF, areaF, q]);

  const toggle = (id) => setSelected(prev => {
    const s = new Set(prev);
    s.has(id) ? s.delete(id) : s.add(id);
    return s;
  });

  const create = async () => {
    if (!selected.size) return;
    setBusy(true); setErr(null);
    try {
      await api.post(`/jobs/case/${caseId}`, {
        partner_ids: [...selected],
        title: title.trim() || null,
        description: desc.trim() || null,
        requires_customer_approval: needsApproval,
      });
      onCreated();
      onClose();
    } catch (e) {
      setErr(e.response?.data?.error || 'Error');
      setBusy(false);
    }
  };

  return (
    <div className={`cs-modal-overlay ${isDark ? 'theme-dark' : 'theme-light'}`} onClick={onClose}>
      <div className="cs-modal jb-modal" onClick={e => e.stopPropagation()}>
        <h2 className="cs-modal-title">{t('jobs.newJob')}</h2>

        <div className="jb-filters">
          <input type="text" placeholder={t('jobs.searchPartner')} value={q}
            onChange={e => setQ(e.target.value)} />
          <select value={catF} onChange={e => setCatF(e.target.value)}>
            <option value="">{t('jobs.allCategories')}</option>
            {cats.map(c => <option key={c.id} value={c.id}>{catName(c.name, language)}</option>)}
          </select>
          <select value={areaF} onChange={e => setAreaF(e.target.value)}>
            <option value="">{t('jobs.allAreas')}</option>
            {areas.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </div>

        <div className="jb-partner-list">
          {partners.map(p => (
            <label key={p.id} className={`jb-partner-row ${selected.has(p.id) ? 'sel' : ''}`}>
              <input type="checkbox" checked={selected.has(p.id)} onChange={() => toggle(p.id)} />
              <span className="jb-partner-info">
                <strong>{p.company_name}</strong>
                <small>
                  {[catName(p.category_name, language), p.areas].filter(Boolean).join(' • ')}
                </small>
              </span>
              <Stars value={p.rating} size={13} />
            </label>
          ))}
          {partners.length === 0 && <p className="cs-empty">—</p>}
        </div>

        <div className="jb-form-fields">
          <label className="cs-field">
            <span>{t('jobs.jobTitle')}</span>
            <input type="text" value={title} onChange={e => setTitle(e.target.value)} />
          </label>
          <label className="cs-field">
            <span>{t('jobs.jobDescription')}</span>
            <textarea rows={2} value={desc} onChange={e => setDesc(e.target.value)} />
          </label>
          <label className="cs-internal-toggle">
            <input type="checkbox" checked={needsApproval} onChange={e => setNeedsApproval(e.target.checked)} />
            {t('jobs.needsApproval')}
          </label>
        </div>

        {err && <div className="cs-modal-error">{err}</div>}
        <div className="cs-modal-actions">
          <span className="jb-sel-count">{t('jobs.selectedCount').replace('{n}', selected.size)}</span>
          <button className="cs-btn-ghost" onClick={onClose}>{t('common.cancel') || 'Cancel'}</button>
          <button className="cs-btn-primary" disabled={busy || !selected.size} onClick={create}>
            {t('jobs.createJobs')}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── EGY JOB KÁRTYA ────────────────────────────────────────────
const JobCard = ({ api, job, t, reload }) => {
  const [open, setOpen]     = useState(false);
  const [detail, setDetail] = useState(null);
  const [notify, setNotify] = useState(true);
  const [busy, setBusy]     = useState(false);
  const [msg, setMsg]       = useState(null);
  const [note, setNote]     = useState('');
  const [fields, setFields] = useState({});

  const loadDetail = useCallback(() => {
    api.get(`/jobs/${job.id}`).then(r => {
      setDetail(r.data);
      setFields({
        quote_amount: r.data.quote_amount ?? '',
        scheduled_at: r.data.scheduled_at ? r.data.scheduled_at.slice(0, 16) : '',
        invoice_number: r.data.invoice_number ?? '',
        invoice_amount: r.data.invoice_amount ?? '',
        due_date: r.data.due_date ? r.data.due_date.slice(0, 10) : '',
      });
    }).catch(() => {});
  }, [api, job.id]);

  useEffect(() => { if (open) loadDetail(); }, [open, loadDetail]);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(null), 3000); };

  const doStatus = async (to) => {
    setBusy(true);
    try {
      await api.post(`/jobs/${job.id}/status`, { status: to, notify_customer: notify });
      await reload(); loadDetail();
    } catch (e) { flash(e.response?.data?.error || 'Error'); }
    finally { setBusy(false); }
  };

  const saveFields = async () => {
    setBusy(true);
    try {
      await api.patch(`/jobs/${job.id}`, {
        quote_amount: fields.quote_amount === '' ? null : fields.quote_amount,
        scheduled_at: fields.scheduled_at === '' ? null : fields.scheduled_at.replace('T', ' ') + ':00',
        invoice_number: fields.invoice_number || null,
        invoice_amount: fields.invoice_amount === '' ? null : fields.invoice_amount,
        due_date: fields.due_date || null,
      });
      await reload(); loadDetail();
      flash('✓');
    } catch (e) { flash(e.response?.data?.error || 'Error'); }
    finally { setBusy(false); }
  };

  const copyTemplate = async () => {
    try {
      const { data } = await api.get(`/jobs/${job.id}/quote-template`);
      await navigator.clipboard.writeText(`To: ${data.to}\nSubject: ${data.subject}\n\n${data.body}`);
      flash(t('jobs.templateCopied'));
    } catch (e) { flash(e.response?.data?.error || 'Error'); }
  };

  const sendEmail = async () => {
    setBusy(true);
    try {
      await api.post(`/jobs/${job.id}/quote-email`, {});
      await reload(); loadDetail();
      flash(t('jobs.emailSent'));
    } catch (e) { flash(e.response?.data?.error || 'Error'); }
    finally { setBusy(false); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try {
      await api.post(`/jobs/${job.id}/notes`, { body: note });
      setNote(''); loadDetail();
    } catch (e) { flash(e.response?.data?.error || 'Error'); }
    finally { setBusy(false); }
  };

  const rate = async (n) => {
    try {
      await api.post(`/jobs/${job.id}/rating`, { rating: n });
      await reload(); loadDetail();
      flash('✓');
    } catch (e) { flash(e.response?.data?.error || 'Error'); }
  };

  const transitions = TRANSITIONS[job.status] || [];

  return (
    <div className="jb-card">
      <div className="jb-card-head" onClick={() => setOpen(o => !o)}>
        <span className="jb-number">{job.job_number}</span>
        <span className="jb-partner">{job.partner_name}</span>
        <span className={jobStatusChipClass(job.status)}>{t(`jobs.status.${job.status}`)}</span>
        {job.quote_amount != null && <span className="jb-amount">{fmtMoney(job.quote_amount, job.quote_currency)}</span>}
        <span className="jb-expand">{open ? '▾' : '▸'}</span>
      </div>

      {open && (
        <div className="jb-card-body">
          {/* Státusz-léptetés */}
          {transitions.length > 0 && (
            <div className="jb-actions-row">
              {transitions.map(to => (
                <button key={to} disabled={busy}
                  className={to === 'cancelled' ? 'cs-btn-danger jb-sm' : 'cs-btn-primary jb-sm'}
                  onClick={() => doStatus(to)}>
                  {t(`jobs.status.${to}`)}
                </button>
              ))}
              <label className="cs-internal-toggle">
                <input type="checkbox" checked={notify} onChange={e => setNotify(e.target.checked)} />
                {t('jobs.notifyCustomer')}
              </label>
            </div>
          )}

          {/* Sablon + email — árajánlat fázisban */}
          {['contact_pending', 'quote_requested', 'quote_rejected'].includes(job.status) && (
            <div className="jb-actions-row">
              <button className="cs-btn-ghost jb-sm" onClick={copyTemplate}>📋 {t('jobs.copyTemplate')}</button>
              <button className="cs-btn-ghost jb-sm" disabled={busy} onClick={sendEmail}>✉️ {t('jobs.sendQuoteEmail')}</button>
            </div>
          )}

          {/* Szerkeszthető mezők */}
          <div className="jb-fields-grid">
            <label className="cs-field">
              <span>{t('jobs.quoteAmount')}</span>
              <input type="number" step="0.01" value={fields.quote_amount}
                onChange={e => setFields(f => ({ ...f, quote_amount: e.target.value }))} />
            </label>
            <label className="cs-field">
              <span>{t('jobs.scheduledAt')}</span>
              <input type="datetime-local" value={fields.scheduled_at}
                onChange={e => setFields(f => ({ ...f, scheduled_at: e.target.value }))} />
            </label>
            <label className="cs-field">
              <span>{t('jobs.invoiceNumber')}</span>
              <input type="text" value={fields.invoice_number}
                onChange={e => setFields(f => ({ ...f, invoice_number: e.target.value }))} />
            </label>
            <label className="cs-field">
              <span>{t('jobs.invoiceAmount')}</span>
              <input type="number" step="0.01" value={fields.invoice_amount}
                onChange={e => setFields(f => ({ ...f, invoice_amount: e.target.value }))} />
            </label>
            <label className="cs-field">
              <span>{t('jobs.dueDate')}</span>
              <input type="date" value={fields.due_date}
                onChange={e => setFields(f => ({ ...f, due_date: e.target.value }))} />
            </label>
            <div className="jb-save-wrap">
              <button className="cs-btn-primary jb-sm" disabled={busy} onClick={saveFields}>💾</button>
            </div>
          </div>

          {/* Partner elérhetőség */}
          {detail && (
            <div className="jb-partner-contact">
              {detail.partner_contact && <span>👤 {detail.partner_contact}</span>}
              {detail.partner_email && <span>✉️ {detail.partner_email}</span>}
              {detail.partner_phone && <span>📞 {detail.partner_phone}</span>}
            </div>
          )}

          {/* Értékelés lezárt joboknál */}
          {RATEABLE.includes(job.status) && (
            <div className="jb-rate-row">
              <span className="cs-status-label-sm">{t('jobs.ratePartner')}</span>
              <Stars value={detail?.my_rating} onChange={rate} size={18} />
            </div>
          )}

          {/* Jegyzetek */}
          <div className="jb-notes">
            <span className="cs-status-label-sm">{t('jobs.notes')}</span>
            {(detail?.notes || []).map(n => (
              <div key={n.id} className="jb-note">
                <small>{n.author_username} • {fmtDT(n.created_at)}</small>
                <div>{n.body}</div>
              </div>
            ))}
            <div className="jb-note-composer">
              <input type="text" value={note} placeholder={t('jobs.addNote')}
                onChange={e => setNote(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addNote()} />
              <button className="cs-btn-ghost jb-sm" disabled={busy} onClick={addNote}>+</button>
            </div>
          </div>

          {/* Történet */}
          {(detail?.history || []).length > 0 && (
            <details className="jb-history">
              <summary>{t('jobs.history')}</summary>
              {detail.history.map(h => (
                <div key={h.id} className="jb-history-row">
                  <small>{fmtDT(h.created_at)}</small>
                  {h.action ? (
                    <span>{ACTION_ICON[h.action] || ''} {t(`jobs.actions.${h.action}`)}</span>
                  ) : (
                    <span>{h.from_status ? `${t(`jobs.status.${h.from_status}`)} → ` : ''}{t(`jobs.status.${h.to_status}`)}</span>
                  )}
                  <small>{h.changed_by_username}{h.customer_notified ? ' 📣' : ''}</small>
                </div>
              ))}
            </details>
          )}

          {msg && <div className="jb-flash">{msg}</div>}
        </div>
      )}
    </div>
  );
};

// ── FŐ SZEKCIÓ ────────────────────────────────────────────────
const CaseJobsSection = ({ api, caseId, t, language, isDark, onJobsLoaded }) => {
  const [jobs, setJobs]     = useState([]);
  const [showModal, setShowModal] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/jobs/case/${caseId}`);
      setJobs(data);
      onJobsLoaded?.(data);
    } catch { /* silent */ }
  }, [api, caseId, onJobsLoaded]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="jb-section">
      <div className="jb-section-head">
        <h3 className="cs-actions-title">🔧 {t('jobs.title')}</h3>
        <button className="cs-btn-ghost jb-sm" onClick={() => setShowModal(true)}>+ {t('jobs.newJob')}</button>
      </div>
      {jobs.length === 0
        ? <p className="cs-empty">{t('jobs.noJobs')}</p>
        : jobs.map(j => <JobCard key={j.id} api={api} job={j} t={t} reload={load} />)}

      {showModal && (
        <NewJobModal api={api} caseId={caseId} t={t} language={language} isDark={isDark}
          onClose={() => setShowModal(false)} onCreated={load} />
      )}
    </div>
  );
};

export default CaseJobsSection;
